import { useDraggable, useDroppable } from "@dnd-kit/core";
import { CircleCheck, CircleX, Trash2, TriangleAlert } from "lucide-react";
import { memo, type ReactNode, useEffect, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import { Button } from "@/shared/components/ui/button";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/shared/components/ui/context-menu";
import { Input } from "@/shared/components/ui/input";
import { cn } from "@/shared/lib/utils";
import {
  useDeleteSplit,
  useRenameSplit,
  useSetSplitNote,
} from "../api/splits.mutations";
import { MAX_NOTE_LENGTH } from "../splits.config";
import {
  layoutSplitSlots,
  type RoleSlots,
  splitDropId,
  splitReorderId,
  splitRuleFailures,
} from "../splits.logic";
import type { BoardCharacter, SplitItem } from "../splits.types";
import { CharacterCard, DraggableCharacterCard } from "./CharacterCard";
import { SplitSummary } from "./SplitSummary";

// One empty reserved role slot (a shorter split's placeholder to keep columns aligned).
function EmptySlot() {
  return (
    <div
      className="flex items-center rounded-md border border-dashed border-border/40 px-2.5 py-1.5 text-sm"
      aria-hidden
    >
      &nbsp;
    </div>
  );
}

// Invisible spacer that keeps a note-less row the same width as one with a note, so the
// card column stays aligned. Used for empty notes and padded reserved slots.
function NoteSpacer() {
  return <div className="w-44 shrink-0" aria-hidden />;
}

// Static note text shown in the drag preview (never editable there).
function StaticNote({ note }: { note: string }) {
  return (
    <div className="flex w-44 shrink-0 items-center truncate px-2 text-xs text-muted-foreground">
      {note}
    </div>
  );
}

// The note beside a card. Nothing renders until a note exists (or editing starts) — an
// existing note shows as clickable text; an empty one is an invisible spacer. `editing`
// is driven by the card's context menu (add/edit); the input is a sibling of the draggable
// node so typing/selecting never starts a drag. Commits on blur/Enter; Escape cancels.
function NoteCell({
  character,
  editing,
  setEditing,
}: {
  character: BoardCharacter;
  editing: boolean;
  setEditing: (editing: boolean) => void;
}) {
  const setNote = useSetSplitNote();
  const [draft, setDraft] = useState(character.note);
  // Escape sets this so the blur it triggers cancels instead of committing.
  const cancelledRef = useRef(false);
  // Seed the draft from the current note whenever editing (re)starts.
  useEffect(() => {
    if (editing) {
      setDraft(character.note);
      cancelledRef.current = false;
    }
  }, [editing, character.note]);

  if (editing) {
    const commit = () => {
      setEditing(false);
      if (cancelledRef.current) return;
      const note = draft.trim();
      if (note !== character.note)
        setNote.mutate({ characterId: character.id, note });
    };
    return (
      <Input
        autoFocus
        value={draft}
        maxLength={MAX_NOTE_LENGTH}
        placeholder="Anteckning"
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
          if (e.key === "Escape") {
            cancelledRef.current = true;
            e.currentTarget.blur();
          }
        }}
        className="h-auto w-44 shrink-0 text-xs"
      />
    );
  }

  if (!character.note) return <NoteSpacer />;
  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="w-44 shrink-0 truncate rounded-md px-2 text-left text-xs text-muted-foreground hover:bg-muted/40"
    >
      {character.note}
    </button>
  );
}

// A live (draggable) row when notes are shown: the card wrapped in a right-click menu to
// add/edit/remove its note, paired with the note cell. Right-click never starts a drag
// (dnd-kit's PointerSensor ignores the secondary button).
function NoteRow({
  card,
  character,
}: {
  card: ReactNode;
  character: BoardCharacter;
}) {
  const setNote = useSetSplitNote();
  const [editing, setEditing] = useState(false);
  const hasNote = character.note.length > 0;
  return (
    <div className="flex items-stretch gap-1.5">
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <div className="min-w-0 flex-1">{card}</div>
        </ContextMenuTrigger>
        <ContextMenuContent>
          <ContextMenuItem onSelect={() => setEditing(true)}>
            {hasNote ? "Redigera anteckning" : "Lägg till anteckning"}
          </ContextMenuItem>
          {hasNote && (
            <ContextMenuItem
              variant="destructive"
              onSelect={() =>
                setNote.mutate({ characterId: character.id, note: "" })
              }
            >
              Ta bort anteckning
            </ContextMenuItem>
          )}
        </ContextMenuContent>
      </ContextMenu>
      <NoteCell
        character={character}
        editing={editing}
        setEditing={setEditing}
      />
    </div>
  );
}

// Renders reserved slots: a card per character (draggable in the live column, static in the
// drag preview) and an EmptySlot for each padded gap that keeps columns aligned. When
// `showNotes` is on, each row pairs the card with its note (right-click a live card to add).
function SlotList({
  slots,
  draggable,
  showNotes,
  highlightedCharacterId,
  remoteDraggingIds,
}: {
  slots: (BoardCharacter | null)[];
  draggable?: boolean;
  showNotes?: boolean;
  highlightedCharacterId?: string;
  remoteDraggingIds?: Set<string>;
}) {
  return (
    <>
      {slots.map((slot, i) => {
        const card = !slot ? (
          <EmptySlot />
        ) : draggable ? (
          <DraggableCharacterCard
            character={slot}
            highlighted={slot.id === highlightedCharacterId}
            remoteDragging={remoteDraggingIds?.has(slot.id)}
          />
        ) : (
          <CharacterCard character={slot} />
        );
        // Empty reserved slots have no id, so fall back to their padded position.
        const key = slot ? slot.id : `empty-${i}`;
        if (!showNotes) return <div key={key}>{card}</div>;
        // Live, editable row with a right-click menu.
        if (slot && draggable)
          return <NoteRow key={key} card={card} character={slot} />;
        // Preview / empty reserved slot: static note text or an invisible spacer.
        return (
          <div key={key} className="flex items-stretch gap-1.5">
            <div className="min-w-0 flex-1">{card}</div>
            {slot?.note ? <StaticNote note={slot.note} /> : <NoteSpacer />}
          </div>
        );
      })}
    </>
  );
}

// Rule-failure footer: green "OK" when clean, else the failures.
// Status colors are the documented exception to semantic tokens.
function SplitStatus({ chars }: { chars: BoardCharacter[] }) {
  const failures = splitRuleFailures(chars);
  return (
    <div className="border-t border-border px-2.5 py-1.5 text-xs">
      {failures.length === 0 ? (
        <div className="flex items-center gap-2 text-emerald-500">
          <CircleCheck className="size-4 shrink-0" />
          OK
        </div>
      ) : (
        <ul className="space-y-1">
          {failures.map((f) => (
            <li
              key={f.id}
              className={cn(
                "flex items-center gap-2",
                f.severity === "error" ? "text-red-500" : "text-amber-500",
              )}
            >
              {f.severity === "error" ? (
                <CircleX className="size-4 shrink-0" />
              ) : (
                <TriangleAlert className="size-4 shrink-0" />
              )}
              <span>{f.label}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Static, non-interactive snapshot of a column — rendered in the drag overlay so the ghost
// looks like the whole split as it is at drag time.
export function SplitColumnPreview({
  split,
  chars,
  roleSlots,
  showNotes,
}: {
  split: SplitItem;
  chars: BoardCharacter[];
  roleSlots: RoleSlots;
  showNotes?: boolean;
}) {
  const slots = layoutSplitSlots(chars, roleSlots);
  return (
    <div
      className={cn(
        "shrink-0 rounded-md border border-border bg-background shadow-lg",
        // w-52 card track + w-44 note + gap-1.5, so the card keeps its no-notes width.
        showNotes ? "w-[calc(13rem+11rem+0.375rem)]" : "w-52",
      )}
    >
      <div className="flex items-center gap-2 border-b border-border px-2.5 py-1.5">
        <span className="min-w-0 flex-1 truncate text-sm font-medium">
          {split.name}
        </span>
        <span className="shrink-0 text-xs text-muted-foreground">
          {chars.length}
        </span>
      </div>
      <div className="flex min-h-16 flex-col gap-1.5 p-1.5">
        <SlotList slots={slots} showNotes={showNotes} />
      </div>
      <SplitSummary chars={chars} />
      <SplitStatus chars={chars} />
    </div>
  );
}

// One split column: a draggable header (drag handle for reordering) with an inline-editable
// name + delete, a droppable list of cards, the armor strip, and the rule-failure footer
// (green "OK" when clean). Delete is gated behind a confirmation dialog.
// Memoized: remote cursor frames re-render the board ~20/s per peer; SplitsBoard hands each column
// referentially-stable props so unaffected columns skip re-rendering (and re-running the rule eval).
export const SplitColumn = memo(function SplitColumn({
  split,
  chars,
  roleSlots,
  showNotes,
  highlightedCharacterId,
  remoteDraggingIds,
}: {
  split: SplitItem;
  chars: BoardCharacter[];
  roleSlots: RoleSlots;
  showNotes?: boolean;
  highlightedCharacterId?: string;
  remoteDraggingIds?: Set<string>;
}) {
  const rename = useRenameSplit();
  const del = useDeleteSplit();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(split.name);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Escape sets this so the blur it triggers cancels instead of committing.
  const cancelledRef = useRef(false);
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: splitDropId(split.id),
  });
  const slots = layoutSplitSlots(chars, roleSlots);
  // Column reordering (core dnd, shared DndContext): the header is the drag handle (source) and
  // a drop target. Dropping one header over another reorders the columns.
  const {
    setNodeRef: setHeaderDragRef,
    attributes,
    listeners,
    isDragging,
  } = useDraggable({
    id: splitReorderId(split.id),
    data: { type: "split", split },
  });
  const { setNodeRef: setHeaderDropRef } = useDroppable({
    id: splitReorderId(split.id),
    data: { type: "split", splitId: split.id },
  });
  const setHeaderRef = (node: HTMLElement | null) => {
    setHeaderDragRef(node);
    setHeaderDropRef(node);
  };

  const startEdit = () => {
    setDraft(split.name);
    cancelledRef.current = false;
    setEditing(true);
  };
  // The single commit path: Enter and Escape both just blur the input, so the
  // rename never fires twice (Enter previously ran commit then re-fired onBlur).
  const commit = () => {
    setEditing(false);
    if (cancelledRef.current) return;
    const name = draft.trim();
    if (name && name !== split.name) rename.mutate({ id: split.id, name });
  };

  return (
    <div
      className={cn(
        "shrink-0 rounded-md border border-border bg-background",
        // w-52 card track + w-44 note + gap-1.5, so the card keeps its no-notes width.
        showNotes ? "w-[calc(13rem+11rem+0.375rem)]" : "w-52",
        isDragging && "opacity-50",
      )}
    >
      <div
        ref={setHeaderRef}
        className={cn(
          "flex items-center gap-2 border-b border-border px-2.5 py-1.5",
          !editing && "cursor-default",
        )}
        {...(editing ? {} : { ...attributes, ...listeners })}
      >
        {editing ? (
          <Input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.currentTarget.blur();
              if (e.key === "Escape") {
                cancelledRef.current = true;
                e.currentTarget.blur();
              }
            }}
            className="h-7 min-w-0 flex-1"
          />
        ) : (
          <button
            type="button"
            onClick={startEdit}
            className="min-w-0 flex-1 truncate text-left text-sm font-medium"
          >
            {split.name}
          </button>
        )}
        <span className="shrink-0 text-xs text-muted-foreground">
          {chars.length}
        </span>
        <Button
          variant="ghost"
          size="icon"
          className="size-7 shrink-0"
          onClick={() => setConfirmDelete(true)}
          aria-label="Ta bort split"
        >
          <Trash2 />
        </Button>
      </div>

      <div
        ref={setDropRef}
        className={cn(
          "flex min-h-16 flex-col gap-1.5 p-1.5",
          isOver && "bg-muted/50",
        )}
      >
        <SlotList
          slots={slots}
          draggable
          showNotes={showNotes}
          highlightedCharacterId={highlightedCharacterId}
          remoteDraggingIds={remoteDraggingIds}
        />
      </div>

      <SplitSummary chars={chars} />

      <SplitStatus chars={chars} />

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ta bort split?</AlertDialogTitle>
            <AlertDialogDescription>
              "{split.name}" tas bort och karaktärerna blir utan split.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={del.isPending}>
              Avbryt
            </AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={del.isPending}
              onClick={() => del.mutate(split.id)}
            >
              <Trash2 />
              Ta bort
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
});
